/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * connectApi -- R13 web Round 1.
 *
 * Thin wrapper around the shared Connect API contract
 * (docs/connect/MOBILE_API.md). Every call is owner-scoped by the
 * server's `requireConnectAccess` middleware; this client never
 * forwards an owner id.
 *
 * `api.get/post/patch/delete` return the raw response body, so we
 * unwrap `.data` once here and throw on `!success`.
 */

import api from './api';

function _unwrap(res, methodLabel) {
  if (!res) {
    const e = new Error(`${methodLabel}: empty response`);
    e.code = 'EMPTY_RESPONSE';
    throw e;
  }
  if (res.success === false) {
    const e = new Error(res.error || `${methodLabel} failed`);
    e.code = res.code || 'SERVER_ERROR';
    e.field = res.field || null;
    throw e;
  }
  return res.data !== undefined ? res.data : res;
}

const connectApi = {
  // ─── Contacts ────────────────────────────────────────────────────
  listContacts({ q, represents, domain, intent, event, status, page, limit } = {}) {
    const qs = new URLSearchParams();
    if (q)          qs.set('q', q);
    if (represents) qs.set('represents', represents);
    if (domain)     qs.set('domain', domain);
    if (intent)     qs.set('intent', intent);
    if (event)      qs.set('event_id', event);
    if (status)     qs.set('status', status);
    if (page)       qs.set('page', String(page));
    if (limit)      qs.set('limit', String(limit));
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return api.get(`/connect/contacts${query}`).then((r) => _unwrap(r, 'listContacts'));
  },
  getContact(id) {
    return api.get(`/connect/contacts/${encodeURIComponent(id)}`).then((r) => _unwrap(r, 'getContact'));
  },
  patchContact(id, patch) {
    return api.patch(`/connect/contacts/${encodeURIComponent(id)}`, patch).then((r) => _unwrap(r, 'patchContact'));
  },
  listEvents() {
    return api.get('/connect/events').then((r) => _unwrap(r, 'listEvents'));
  },

  // ─── Facts (What we know) ────────────────────────────────────────
  listFacts(contactId, { kind, shareable } = {}) {
    const qs = new URLSearchParams();
    if (kind)                 qs.set('kind', kind);
    if (shareable !== undefined && shareable !== null) qs.set('shareable', String(shareable));
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return api.get(`/connect/contacts/${encodeURIComponent(contactId)}/facts${query}`)
      .then((r) => _unwrap(r, 'listFacts'));
  },
  patchFact(factId, patch) {
    return api.patch(`/connect/facts/${encodeURIComponent(factId)}`, patch).then((r) => _unwrap(r, 'patchFact'));
  },

  // ─── Feedback ────────────────────────────────────────────────────
  listFeedback({ status, contact_id, limit } = {}) {
    const qs = new URLSearchParams();
    if (status)     qs.set('status', status);
    if (contact_id) qs.set('contact_id', contact_id);
    if (limit)      qs.set('limit', String(limit));
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return api.get(`/connect/feedback${query}`).then((r) => _unwrap(r, 'listFeedback'));
  },
  getFeedback(id) {
    return api.get(`/connect/feedback/${encodeURIComponent(id)}`).then((r) => _unwrap(r, 'getFeedback'));
  },
  getFeedbackThread(id) {
    return api.get(`/connect/feedback/${encodeURIComponent(id)}/thread`).then((r) => _unwrap(r, 'getFeedbackThread'));
  },
  patchFeedbackStatus(id, status) {
    return api.patch(`/connect/feedback/${encodeURIComponent(id)}`, { status })
      .then((r) => _unwrap(r, 'patchFeedbackStatus'));
  },
  replyToFeedback(id, { subject, body_text, body_html } = {}) {
    return api.post(`/connect/feedback/${encodeURIComponent(id)}/reply`, { subject, body_text, body_html })
      .then((r) => _unwrap(r, 'replyToFeedback'));
  },
  noteOnFeedback(id, body_text) {
    return api.post(`/connect/feedback/${encodeURIComponent(id)}/note`, { body_text })
      .then((r) => _unwrap(r, 'noteOnFeedback'));
  },
  logFeedbackForContact(contactId, {
    type, source, text, step_index, rating, extra, pitch_id
  }) {
    return api.post(`/connect/contacts/${encodeURIComponent(contactId)}/feedback`, {
      type, source, text, step_index, rating, extra, pitch_id
    }).then((r) => _unwrap(r, 'logFeedbackForContact'));
  },
  listFeedbackForContact(contactId, { limit } = {}) {
    const qs = new URLSearchParams();
    if (limit) qs.set('limit', String(limit));
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return api.get(`/connect/contacts/${encodeURIComponent(contactId)}/feedback${query}`)
      .then((r) => _unwrap(r, 'listFeedbackForContact'));
  },

  // ─── Owner settings ──────────────────────────────────────────────
  getSettings() {
    return api.get('/connect/settings/me').then((r) => _unwrap(r, 'getSettings'));
  },
  patchSettings(patch) {
    return api.patch('/connect/settings/me', patch).then((r) => _unwrap(r, 'patchSettings'));
  },

  // ─── Quick capture (Round 3 uses more, Round 1 only needs this
  // for "Create contact from feedback"). ──────────────────────────
  quickCapture(payload) {
    return api.post('/connect/quick-capture', payload).then((r) => _unwrap(r, 'quickCapture'));
  },

  // ─── Smart Invite (R13 web Round 2) ──────────────────────────────
  // Pitch generate -- mint a draft (idempotent while draft exists).
  // Response status is 201 (sync-complete) or 202 (background cooking);
  // the client should rely on data.roadmap_status, not the HTTP code.
  generatePitch(contactId) {
    return api.post(`/connect/contacts/${encodeURIComponent(contactId)}/pitch/generate`, {})
      .then((r) => _unwrap(r, 'generatePitch'));
  },
  getPitch(pitchId) {
    return api.get(`/connect/pitches/${encodeURIComponent(pitchId)}`)
      .then((r) => _unwrap(r, 'getPitch'));
  },
  patchPitch(pitchId, patch) {
    return api.patch(`/connect/pitches/${encodeURIComponent(pitchId)}`, patch)
      .then((r) => _unwrap(r, 'patchPitch'));
  },
  regeneratePitch(pitchId, { refreshItems = false } = {}) {
    const qs = refreshItems ? '?refresh_items=true' : '';
    return api.post(`/connect/pitches/${encodeURIComponent(pitchId)}/regenerate${qs}`, {})
      .then((r) => _unwrap(r, 'regeneratePitch'));
  },
  previewPitch(pitchId) {
    return api.post(`/connect/pitches/${encodeURIComponent(pitchId)}/preview`, {})
      .then((r) => _unwrap(r, 'previewPitch'));
  },
  previewLinkPitch(pitchId) {
    return api.post(`/connect/pitches/${encodeURIComponent(pitchId)}/preview-link`, {})
      .then((r) => _unwrap(r, 'previewLinkPitch'));
  },
  sendPitch(pitchId, { channel = 'email', allow_despite_intro = false } = {}) {
    return api.post(`/connect/pitches/${encodeURIComponent(pitchId)}/send`, {
      channel,
      allow_despite_intro: !!allow_despite_intro
    }).then((r) => _unwrap(r, 'sendPitch'));
  }
};

export default connectApi;
